// Globals
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Input, InputNumber, Select, TextEditor } from '@/ui';
import { FormItem, useWatchForm } from '@/components';
import FormSectionHeader from '../FormSectionHeader/FormSectionHeader';
import { StyledFormSectionGrid, StyledFullWidthGidItem } from '../Styled';
import { StyledTextEditorFormItem } from './FreeDeliveryFormSectionStyles';

// Helpers
import { useDeliverySettingsFormRules } from '../../hooks';
import { DeliverySettingsFormFields, TDeliverySettingsFormSectionProps } from '../../helpers';

const FreeDeliveryFormSection: FC<TDeliverySettingsFormSectionProps> = ({
  formInstance,
  americanStates,
  canadianStates,
}) => {
  const { t } = useTranslation(['common']);

  const isFreeDeliveryEnabled = useWatchForm(
    [DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.IS_ENABLED],
    formInstance,
  );

  const rules = useDeliverySettingsFormRules(formInstance);

  const handleToggleFreeDelivery = () => formInstance.validateFields();

  return (
    <div>
      <FormSectionHeader
        fieldName={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.IS_ENABLED]}
        rules={rules.freeDelivery[DeliverySettingsFormFields.IS_ENABLED]}
        title={t('free_delivery')}
        onToggle={handleToggleFreeDelivery}
      />
      <StyledFormSectionGrid>
        <FormItem
          name={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.MIN_ORDER_AMOUNT]}
          label={t('delivery_min_amount_label')}
          rules={rules.freeDelivery[DeliverySettingsFormFields.MIN_ORDER_AMOUNT]}
        >
          <InputNumber disabled={!isFreeDeliveryEnabled} placeholder={t('delivery_min_amount_placeholder')} />
        </FormItem>
        <FormItem
          label={t('excluded_states_usa_label')}
          name={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.EXCLUDED_STATES_USA]}
        >
          <Select
            disabled={!isFreeDeliveryEnabled}
            options={americanStates}
            isMultiselect
            placeholder={t('excluded_states_usa_placeholder')}
          />
        </FormItem>
        <FormItem
          label={t('excluded_states_canada_label')}
          name={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.EXCLUDED_STATES_CANADA]}
        >
          <Select
            disabled={!isFreeDeliveryEnabled}
            options={canadianStates}
            isMultiselect
            placeholder={t('excluded_states_canada_placeholder')}
          />
        </FormItem>
        <StyledFullWidthGidItem
          label={t('delivery_free_shipping_title_label')}
          name={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.POPUP_TITLE]}
          rules={rules.freeDelivery[DeliverySettingsFormFields.POPUP_TITLE]}
        >
          <Input disabled={!isFreeDeliveryEnabled} placeholder={t('delivery_free_shipping_title_placeholder')} />
        </StyledFullWidthGidItem>
        <StyledTextEditorFormItem
          label={t('delivery_free_shipping_description_label')}
          rules={rules.freeDelivery[DeliverySettingsFormFields.POPUP_DESCRIPTION]}
          name={[DeliverySettingsFormFields.FREE_DELIVERY, DeliverySettingsFormFields.POPUP_DESCRIPTION]}
        >
          <TextEditor
            readOnly={!isFreeDeliveryEnabled}
            placeholder={t('delivery_free_shipping_description_placeholder')}
          />
        </StyledTextEditorFormItem>
      </StyledFormSectionGrid>
    </div>
  );
};

export default FreeDeliveryFormSection;
